import type { TContestGenre, TRequirements } from '../../../types/global/helper';

/** Backend `contest_genre` enum values (mongoose). */
export const CONTEST_API_GENRES: Array<{ label: string; value: TContestGenre }> = [
    { label: 'Education', value: 'education' },
    { label: 'Edutainment', value: 'edutainment' },
    { label: 'Open Air Casting', value: 'open air casting' },
    { label: 'Acting', value: 'acting' },
    { label: 'Creative Advert', value: 'creative advert' },
    { label: 'TV Advertisement', value: 'tv advertisement' },
    { label: 'Expressions', value: 'expressions' },
    { label: 'Music', value: 'music' },
    { label: 'Artistic Expression', value: 'artistic expression' },
    { label: 'Film Festival', value: 'film festival' },
    { label: 'Music Festival', value: 'music festival' },
];

/** Backend `contest_tags` enum values (mongoose). */
export const CONTEST_API_TAGS: Array<{ label: string; value: string }> = [
    { label: 'Short Film', value: 'short film' },
    { label: 'Music Video', value: 'music video' },
    { label: 'Dance', value: 'dance' },
    { label: 'Drama', value: 'drama' },
    { label: 'Comedy', value: 'comedy' },
    { label: 'Fashion', value: 'fashion' },
    { label: 'VFX', value: 'vfx' },
    { label: 'Sci-Fi', value: 'sci-fi' },
    { label: 'Generic', value: 'generic' },
    { label: 'Festival', value: 'festival' },
];

const GENRE_LABEL_TO_API: Record<string, TContestGenre> = Object.fromEntries(
    CONTEST_API_GENRES.flatMap(g => [
        [g.value, g.value],
        [g.label, g.value],
        [g.label.toLowerCase(), g.value],
    ]),
) as Record<string, TContestGenre>;

const TAG_LABEL_TO_API: Record<string, string> = Object.fromEntries(
    CONTEST_API_TAGS.flatMap(t => [
        [t.value, t.value],
        [t.label, t.value],
        [t.label.toLowerCase(), t.value],
    ]),
);

/** Map legacy wizard labels (e.g. "Education") to API enum values. */
export function mapWizardGenresToApi(genres: string[]): TContestGenre[] {
    const out: TContestGenre[] = [];
    for (const g of genres) {
        const key = g?.trim();
        if (!key) continue;
        const mapped = GENRE_LABEL_TO_API[key] ?? GENRE_LABEL_TO_API[key.toLowerCase()];
        if (mapped && !out.includes(mapped)) out.push(mapped);
    }
    return out.length ? out : ['acting'];
}

/** Map display tags to API `contest_tags` enum values. */
export function mapWizardTagsToApi(tags: string[]): string[] {
    const out: string[] = [];
    for (const t of tags) {
        const key = t?.trim();
        if (!key) continue;
        const mapped = TAG_LABEL_TO_API[key] ?? TAG_LABEL_TO_API[key.toLowerCase()];
        if (mapped && !out.includes(mapped)) out.push(mapped);
    }
    return out;
}

/** API expects `{ header, detail }[]`, not plain strings. */
export function mapWizardRequirementsToApi(requirements: string[]): TRequirements[] {
    return requirements
        .map(r => r?.trim())
        .filter(Boolean)
        .map(text => {
            const lines = text
                .split('\n')
                .map(l => l.trim())
                .filter(Boolean);
            if (lines.length <= 1) {
                return { header: lines[0] || text, detail: '' };
            }
            return { header: lines[0], detail: lines.slice(1).join('\n') };
        });
}
